import { z } from 'zod';
/**
 * Схемы валидации модели. Они же — единственный источник правды о форме данных:
 * типы в `types.ts` выводятся отсюда, поэтому клиент и сервер физически не могут
 * разойтись в понимании модели (раздел 8 platform-architecture.md).
 */
export const TASK_STATUSES = ['backlog', 'wip', 'done'];
export const SPRINT_TYPES = ['week', 'month'];
/**
 * `system` — правки самого сервера: автоперенос незакрытых в новый спринт
 * (3.11.6). Отдельный источник, а не `import`, чтобы в журнале было видно, что
 * задачу двигал не человек и не агент.
 */
export const SOURCES = ['human', 'agent', 'import', 'system'];
export const ENTITIES = ['task', 'checklist_item', 'task_link', 'task_tag', 'settings'];
/**
 * `purge` — «стереть насовсем» из корзины (3.18.6). Отдельный вид, а не
 * повторное `delete`: тот лишь обновил бы время удаления, то есть продлил бы
 * задаче жизнь ровно там, где её просят прекратить.
 */
export const MUTATION_KINDS = ['upsert', 'delete', 'purge'];
/**
 * Повтор задачи (3.19): каждую неделю или каждый месяц. Чаще спринта он не
 * срабатывает — это правило `repeatPeriod`, а не схемы: настройка спринта
 * меняется, а повтор в задаче остаётся тем, что выбрал человек.
 */
export const REPEATS = ['week', 'month'];
export const taskStatusSchema = z.enum(TASK_STATUSES);
export const sprintTypeSchema = z.enum(SPRINT_TYPES);
export const sourceSchema = z.enum(SOURCES);
export const entitySchema = z.enum(ENTITIES);
export const mutationKindSchema = z.enum(MUTATION_KINDS);
export const repeatSchema = z.enum(REPEATS);
/** Ключ спринта считается из даты и потому имеет ровно две формы. */
export const sprintKeySchema = z
    .string()
    .regex(/^(w:\d{4}-\d{2}-\d{2}|m:\d{4}-\d{2})$/, 'ключ спринта — w:ГГГГ-ММ-ДД или m:ГГГГ-ММ');
/** Дата без времени: срок задаётся датой (3.6.2 требований). */
export const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'дата в форме ГГГГ-ММ-ДД');
/**
 * Время суток без зоны, отдельно от даты: оно не обязательно и лишь уточняет
 * её (3.6.2). Настенное, как и дата: «сегодня» считается по устройству, и
 * ресторан в 17:00 остаётся в 17:00, куда бы ни улетел телефон.
 */
export const timeSchema = z.string().regex(/^\d{2}:\d{2}$/, 'время в форме ЧЧ:ММ');
export const uuidSchema = z.uuid();
/**
 * Гибридные логические часы: `<миллисекунды>-<счётчик>-<устройство>` в base36,
 * с фиксированной шириной первых двух частей — чтобы сравнение строк совпадало
 * со сравнением времени.
 */
export const hlcSchema = z
    .string()
    .regex(/^[0-9a-z]{11}-[0-9a-z]{4}-[0-9a-z-]+$/i, 'некорректный HLC');
export const fieldClocksSchema = z.record(z.string(), hlcSchema);
/** Дробный индекс порядка: строка в base62, между любыми двумя есть третья. */
export const orderKeySchema = z.string().min(1);
const timestampSchema = z.iso.datetime({ offset: true });
/** Зашифрованное значение: конверт лежит в базе как bytea, по проводу — base64. */
export const envelopeSchema = z.base64();
export const taskSchema = z.object({
    id: uuidSchema,
    user_id: uuidSchema,
    number: z.number().int().positive(),
    title: envelopeSchema.nullable(),
    notes: envelopeSchema.nullable(),
    status: taskStatusSchema,
    sprint: sprintKeySchema.nullable(),
    due: dateSchema.nullable(),
    /**
     * Время без даты бессмысленно, но схема такую пару не отвергает: правки
     * сливаются по полям, и на гонке двух устройств (одно сняло дату, другое
     * поставило время) она возникает законно. Отвергнутая запись уехала бы в
     * карантин и пропала с экрана — как уже было с тегами. Чтение просто не
     * показывает время при пустой дате, см. `dueTimeOf` в правилах.
     */
    due_time: timeSchema.nullable(),
    parent_id: uuidSchema.nullable(),
    /**
     * Повтор есть только у последнего экземпляра серии: сервер, заведя копию,
     * снимает его с прошлого той же правкой (3.19.6). Так «голова» серии одна, и
     * стёртая из корзины копия не оживит прошлые экземпляры.
     */
    repeat: repeatSchema.nullable(),
    /**
     * Серия — id её первого экземпляра. Остаётся у прошлых экземпляров, когда
     * повтор с них уже снят: по ней автоперенос узнаёт, что задачу переносить
     * не надо (3.19.3), а карточка — что галочки повтора у неё быть не должно.
     */
    series_id: uuidSchema.nullable(),
    order_key: orderKeySchema,
    created_at: timestampSchema,
    updated_at: timestampSchema,
    done_at: timestampSchema.nullable(),
    deleted_at: timestampSchema.nullable(),
    created_by: sourceSchema,
    field_clocks: fieldClocksSchema,
    last_seq: z.number().int().nonnegative(),
});
export const checklistItemSchema = z.object({
    id: uuidSchema,
    user_id: uuidSchema,
    task_id: uuidSchema,
    text: envelopeSchema.nullable(),
    done: z.boolean(),
    order_key: orderKeySchema,
    created_at: timestampSchema,
    deleted_at: timestampSchema.nullable(),
    field_clocks: fieldClocksSchema,
    last_seq: z.number().int().nonnegative(),
});
export const taskLinkSchema = z.object({
    id: uuidSchema,
    user_id: uuidSchema,
    blocker_id: uuidSchema,
    blocked_id: uuidSchema,
    created_at: timestampSchema,
    deleted_at: timestampSchema.nullable(),
    field_clocks: fieldClocksSchema,
    last_seq: z.number().int().nonnegative(),
});
export const taskTagSchema = z.object({
    id: uuidSchema,
    user_id: uuidSchema,
    task_id: uuidSchema,
    tag: envelopeSchema.nullable(),
    tag_bidx: z.base64().nullable(),
    created_at: timestampSchema,
    deleted_at: timestampSchema.nullable(),
    field_clocks: fieldClocksSchema,
    last_seq: z.number().int().nonnegative(),
});
export const settingsSchema = z.object({
    user_id: uuidSchema,
    sprint_type: sprintTypeSchema,
    field_clocks: fieldClocksSchema,
    last_seq: z.number().int().nonnegative(),
});
/**
 * Мутация — единственная форма записи: и клиентская правка, и вызов
 * прикладного REST разворачиваются в неё (раздел 5.1 архитектуры).
 */
export const mutationSchema = z.object({
    op_id: uuidSchema,
    hlc: hlcSchema,
    device_id: z.string().min(1),
    source: sourceSchema,
    entity: entitySchema,
    entity_id: uuidSchema,
    kind: mutationKindSchema,
    fields: z.record(z.string(), z.unknown()),
});
/** Пачка мутаций: до 500 штук, ограничение по размеру проверяется транспортом. */
export const pushRequestSchema = z.object({
    device_id: z.string().min(1),
    ops: z.array(mutationSchema).max(500),
    /**
     * Попросить следующий блок номеров задач. Просит клиент, а не решает сервер:
     * сколько номеров осталось у устройства, знает только оно само.
     */
    want_numbers: z.boolean().optional(),
});
export const pushResponseSchema = z.object({
    applied: z.array(z.object({ op_id: uuidSchema, seq: z.number().int().positive() })),
    rejected: z.array(z.object({ op_id: uuidSchema, reason: z.string() })),
    cursor: z.number().int().nonnegative(),
    number_lease: z
        .object({ from_number: z.number().int().positive(), to_number: z.number().int().positive() })
        .optional(),
});
/**
 * pull отдаёт не мутации, а текущее состояние изменившихся записей вместе с их
 * часами: клиент сливает полученное тем же кодом, что и локальные правки
 * (раздел 5.2 архитектуры).
 */
export const pullChangeSchema = z.object({
    entity: entitySchema,
    entity_id: uuidSchema,
    seq: z.number().int().positive(),
    record: z.unknown(),
});
export const pullResponseSchema = z.object({
    changes: z.array(pullChangeSchema),
    cursor: z.number().int().nonnegative(),
    has_more: z.boolean(),
});
/**
 * Полный снимок: путь для устройства, чей курсор ушёл ниже watermark, и для
 * первого запуска. Записи с надгробиями тоже приезжают — иначе устройство не
 * узнает, что задача удалена.
 */
export const bootstrapResponseSchema = z.object({
    snapshot: z.object({
        tasks: z.array(z.unknown()),
        checklist_items: z.array(z.unknown()),
        task_links: z.array(z.unknown()),
        task_tags: z.array(z.unknown()),
        settings: z.unknown(),
    }),
    cursor: z.number().int().nonnegative(),
});
//# sourceMappingURL=schema.js.map